import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { completeMove, createEnrollment, currentMove } from '@/domains/breakouts/engine';
import { getBreakoutById } from '@/domains/breakouts/catalog';
import {
  applyInterventionToMomentum,
  applyMoveToMomentum,
  DEFAULT_MOMENTUM,
} from '@/domains/momentum/engine';
import { buildHottieContext, resolveImCloseAction } from '@/domains/imClose/engine';
import {
  DEFAULT_HOTTIE_PERMISSION,
  disableHottieSharing,
  enableHottieSharing,
  setHottieFlag,
} from '@/domains/hottie/permissions';
import type {
  FocusAreaId,
  Goal,
  GoalScope,
  InterventionStyle,
  MeProfile,
  MeState,
  MoveOutcome,
  NudgePreferences,
} from '@/domains/types';

const newId = (prefix: string) =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const defaultNudge: NudgePreferences = {
  style: 'practical',
  quietHoursStart: null,
  quietHoursEnd: null,
  channels: ['in_app'],
};

const emptyState = (): Omit<MeState, 'identity'> & {
  identity: MeState['identity'];
} => ({
  profile: null,
  goals: [],
  enrollments: [],
  moveRecords: [],
  momentum: [...DEFAULT_MOMENTUM],
  reflections: [],
  interventions: [],
  hottiePermission: { ...DEFAULT_HOTTIE_PERMISSION },
  nudgePreferences: { ...defaultNudge },
  events: [],
  identity: {
    mode: 'local',
    userId: `local_${typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : 'dev'}`,
  },
});

interface MeActions {
  ensureIdentity: () => void;
  setIdentityFromAuth: (input: {
    mode: 'local' | 'supabase';
    userId: string;
    email?: string | null;
  }) => void;
  completeOnboarding: (input: {
    focusAreaIds: FocusAreaId[];
    stuckSummary: string;
    differentSummary: string;
    helpStyle: InterventionStyle;
    displayName?: string;
  }) => void;
  addGoal: (input: {
    focusAreaId: FocusAreaId;
    statement: string;
    desiredChange: string;
    scope: GoalScope;
  }) => Goal;
  enrolInBreakout: (breakoutId: string, goalId: string) => void;
  recordMove: (input: {
    outcome: MoveOutcome;
    difficulty?: 1 | 2 | 3 | 4 | 5;
    note?: string;
  }) => void;
  runImClose: (triggerKey?: string) => {
    title: string;
    action: string;
    why: string;
    interventionId: string;
  };
  markInterventionHelped: (id: string, helped: boolean) => void;
  setHottieEnabled: (enabled: boolean) => void;
  setHottieShareFlag: (
    flag:
      | 'shareFocusAreas'
      | 'shareActiveBreakout'
      | 'shareGoals'
      | 'shareMomentum'
      | 'shareTriggers'
      | 'shareNudgePrefs',
    value: boolean,
  ) => void;
  getHottieContext: () => Record<string, unknown> | null;
  activeEnrollment: () => MeState['enrollments'][number] | null;
  resetLocal: () => void;
}

export type MeStore = MeState & MeActions;

export const useMeStore = create<MeStore>()(
  persist(
    (set, get) => ({
      ...emptyState(),

      ensureIdentity: () => {
        const { identity } = get();
        if (!identity.userId) {
          set({
            identity: {
              mode: 'local',
              userId: `local_${crypto.randomUUID()}`,
            },
          });
        }
      },

      setIdentityFromAuth: (input) => {
        set({
          identity: {
            mode: input.mode,
            userId: input.userId,
          },
        });
      },

      completeOnboarding: (input) => {
        const now = new Date().toISOString();
        const profile: MeProfile = {
          onboardingComplete: true,
          displayName: input.displayName?.trim() || null,
          focusAreaIds: input.focusAreaIds,
          helpStyle: input.helpStyle,
          stuckSummary: input.stuckSummary.trim(),
          differentSummary: input.differentSummary.trim(),
          createdAt: get().profile?.createdAt ?? now,
          updatedAt: now,
        };
        set({
          profile,
          nudgePreferences: {
            ...get().nudgePreferences,
            style: input.helpStyle,
          },
          events: [
            ...get().events,
            {
              id: newId('evt'),
              type: 'onboarding_complete',
              payload: { focusAreaIds: input.focusAreaIds },
              safetyClass: input.focusAreaIds.includes('chems') ? 'substance' : 'standard',
              createdAt: now,
            },
          ],
        });
      },

      addGoal: (input) => {
        const goal: Goal = {
          id: newId('goal'),
          focusAreaId: input.focusAreaId,
          statement: input.statement.trim(),
          desiredChange: input.desiredChange.trim(),
          scope: input.scope,
          createdAt: new Date().toISOString(),
        };
        set({ goals: [...get().goals, goal] });
        return goal;
      },

      enrolInBreakout: (breakoutId, goalId) => {
        const breakout = getBreakoutById(breakoutId);
        if (!breakout || breakout.moves.length === 0) {
          throw new Error('Breakout is not ready for enrolment yet.');
        }
        const enrollment = createEnrollment(breakoutId, goalId);
        set({
          enrollments: [
            ...get().enrollments.map((e) =>
              e.status === 'active' ? { ...e, status: 'paused' as const } : e,
            ),
            enrollment,
          ],
          events: [
            ...get().events,
            {
              id: newId('evt'),
              type: 'breakout_enrolled',
              payload: { breakoutId, goalId },
              safetyClass: breakout.safetyClass,
              createdAt: new Date().toISOString(),
            },
          ],
        });
      },

      recordMove: (input) => {
        const enrollment = get().activeEnrollment();
        if (!enrollment) throw new Error('No active Breakout.');
        const current = currentMove(enrollment);
        if (!current) throw new Error('No Move for current day.');

        const { record, nextEnrollment } = completeMove({
          enrollment,
          moveTemplateId: current.move.id,
          day: enrollment.currentDay,
          outcome: input.outcome,
          difficulty: input.difficulty,
          note: input.note,
        });

        set({
          moveRecords: [...get().moveRecords, record],
          enrollments: get().enrollments.map((e) =>
            e.id === nextEnrollment.id ? nextEnrollment : e,
          ),
          momentum: applyMoveToMomentum(get().momentum, input.outcome, current.move.id),
          events: [
            ...get().events,
            {
              id: newId('evt'),
              type: 'move_recorded',
              payload: {
                outcome: input.outcome,
                day: enrollment.currentDay,
                moveId: current.move.id,
              },
              safetyClass: current.move.safetyClass,
              createdAt: record.recordedAt,
            },
          ],
        });
      },

      runImClose: (triggerKey = 'open_app') => {
        const state = get();
        const enrollment = state.activeEnrollment();
        const resolved = resolveImCloseAction({
          profile: state.profile,
          activeEnrollment: enrollment,
          recentMoves: state.moveRecords.slice(-5),
          style: state.nudgePreferences.style,
          triggerKey,
        });
        const interventionId = newId('int');
        set({
          interventions: [
            ...state.interventions,
            {
              id: interventionId,
              enrollmentId: enrollment?.id ?? null,
              triggerKey: resolved.triggerKey,
              actionOffered: resolved.action,
              helped: null,
              createdAt: new Date().toISOString(),
            },
          ],
        });
        return {
          title: resolved.title,
          action: resolved.action,
          why: resolved.why,
          interventionId,
        };
      },

      markInterventionHelped: (id, helped) => {
        const interventions = get().interventions.map((i) =>
          i.id === id ? { ...i, helped } : i,
        );
        const hit = interventions.find((i) => i.id === id);
        set({
          interventions,
          momentum: hit
            ? applyInterventionToMomentum(get().momentum, { helped })
            : get().momentum,
        });
      },

      setHottieEnabled: (enabled) => {
        set({
          hottiePermission: enabled
            ? enableHottieSharing(get().hottiePermission)
            : disableHottieSharing(get().hottiePermission),
        });
      },

      setHottieShareFlag: (flag, value) => {
        set({
          hottiePermission: setHottieFlag(get().hottiePermission, flag, value),
        });
      },

      getHottieContext: () => {
        const state = get();
        const enrollment = state.activeEnrollment();
        const breakout = enrollment ? getBreakoutById(enrollment.breakoutId) : null;
        return buildHottieContext(state.hottiePermission, {
          focusAreaIds: state.profile?.focusAreaIds ?? [],
          activeBreakoutTitle: breakout?.title ?? null,
          goals: state.goals.map((g) => g.statement),
          momentumSummary: state.momentum.map((m) => `${m.label}: ${m.lifetime}`),
          triggers: state.interventions.slice(-5).map((i) => i.triggerKey),
          nudgeStyle: state.nudgePreferences.style,
        });
      },

      activeEnrollment: () =>
        get().enrollments.find((e) => e.status === 'active') ?? null,

      resetLocal: () => set(emptyState()),
    }),
    {
      name: 'hottr.me.v1',
      partialize: (s) => ({
        profile: s.profile,
        goals: s.goals,
        enrollments: s.enrollments,
        moveRecords: s.moveRecords,
        momentum: s.momentum,
        reflections: s.reflections,
        interventions: s.interventions,
        hottiePermission: s.hottiePermission,
        nudgePreferences: s.nudgePreferences,
        events: s.events.slice(-200),
        identity: s.identity,
      }),
    },
  ),
);
